import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Modal, message, Tag, Card,
  Switch, Popconfirm, Input, Select, Form, Row, Col, Image, Alert, Spin
} from 'antd';
import {
  ShopOutlined, PlusOutlined, DeleteOutlined,
  EditOutlined, SearchOutlined, ArrowRightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { manualProductConnectionAPI, dropshipperAPI, API_BASE_URL } from '../api';
import { BRAND_PRIMARY } from '../utils/brandColors';
import ProductSearchSelector from '../utils/ProductSearchSelector';
import ShopifyIcon from './Utils/ShopifyIcon';
import WooCommerceIcon from './Utils/WooCommerceIcon';

const { Option } = Select;
const { Search } = Input;

const getPlatformIcon = (platform) => {
  switch ((platform || '').toLowerCase()) {
    case 'shopify':
      return <ShopifyIcon />;
    case 'woocommerce':
      return <WooCommerceIcon />;
    default:
      return <ShopOutlined />;
  }
};

const renderPlatformTag = (platform) => {
  if (!platform) {
    return '-';
  }

  let style = {};
  const lowerPlatform = (platform || '').toLowerCase();
  if (lowerPlatform === 'woocommerce') {
    style = { background: '#f3e5f5', borderColor: '#9c27b0', color: '#6a1b9a' };
  } else if (lowerPlatform === 'shopify') {
    style = { backgroundColor: '#f0f9f0', borderColor: '#96bf48', color: '#2d5a1d' };
  } else {
    style = { backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: '#000000' };
  }

  return (
    <Tag
      icon={getPlatformIcon(platform)}
      style={style}
    >
      {platform.toUpperCase()}
    </Tag>
  );
};

const ManualProductConnect = () => {
  const navigate = useNavigate();
  
  // 连接列表状态
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  // 连接商品流程状态
  const [step, setStep] = useState(0); // 0: 选择平台, 1: 选择Shopify商品, 2: 配置映射（包含选择本地商品）
  const [connectModalVisible, setConnectModalVisible] = useState(false);
  const [connectedStores, setConnectedStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  
  // Shopify商品列表状态
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [shopifySearch, setShopifySearch] = useState('');
  const [shopifyPagination, setShopifyPagination] = useState({
    hasNextPage: false,
    endCursor: null,
  });
  const [shopifyCursor, setShopifyCursor] = useState(null);
  const [selectedShopifyProduct, setSelectedShopifyProduct] = useState(null);
  
  // 本系统商品状态
  const [selectedLocalProduct, setSelectedLocalProduct] = useState(null);
  const [productSelectorVisible, setProductSelectorVisible] = useState(false);
  
  // 配置表单状态
  const [configForm] = Form.useForm();
  const [freightChannels, setFreightChannels] = useState([]);
  const [myCustomizations, setMyCustomizations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // 编辑连接对话框（复用配置流程）
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  
  useEffect(() => {
    fetchConnections();
    fetchConnectedStores();
    fetchFreightChannels();
    fetchMyCustomizations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchConnections = async (page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      const response = await manualProductConnectionAPI.getAll({
        page,
        page_size: pageSize,
      });
      
      if (response.data.success) {
        setConnections(response.data.data || []);
        setPagination({
          current: response.data.page || page,
          pageSize: response.data.page_size || pageSize,
          total: response.data.total || 0,
        });
      }
    } catch (error) {
      
      message.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectedStores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/stores/integration`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        const normalizedStores = (data.data || []).map((entry) => {
          const storeData = entry.data || {};
          return {
            id: entry.id,
            dropshipper_id: entry.dropshipper_id,
            platform: entry.platform || storeData.platform,
            shop_domain: entry.shop_domain || storeData.shop_domain,
            created_at: entry.created_at,
            updated_at: entry.updated_at,
            data: storeData,
          };
        });
        setConnectedStores(normalizedStores);
      }
    } catch (error) {
      
    }
  };

  const fetchFreightChannels = async () => {
    try {
      const response = await dropshipperAPI.getFreightChannels();
      setFreightChannels(response.data);
    } catch (error) {
      
    }
  };

  const fetchMyCustomizations = async () => {
    try {
      const response = await dropshipperAPI.getMyCustomizations();
      setMyCustomizations(response.data);
    } catch (error) {
      
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleTableChange = (paginationConfig) => {
    fetchConnections(paginationConfig.current, paginationConfig.pageSize);
  };

  // 开始连接流程
  const handleStartConnect = () => {
    setStep(0);
    setSelectedStore(null);
    setSelectedShopifyProduct(null);
    setSelectedLocalProduct(null);
    setShopifySearch('');
    setShopifyCursor(null);
    configForm.resetFields();
    setConnectModalVisible(true);
  };

  // 选择平台
  const handleSelectStore = (store) => {
    setSelectedStore(store);
    setStep(1);
    fetchExternalProducts(store.platform, store.shop_domain);
  };

  // 获取外部商店商品列表（支持 Shopify 和 WooCommerce）
  const fetchExternalProducts = async (platform, shopDomain, cursor = null, search = '') => {
    try {
      setShopifyLoading(true);
      const params = {
        page_size: 10,
      };
      if (cursor) params.cursor = cursor;
      if (search) params.search = search;
      
      const response = await manualProductConnectionAPI.getExternalProducts(platform, shopDomain, params);
      
      if (response.data.success) {
        setShopifyProducts(response.data.products || []);
        setShopifyPagination({
          hasNextPage: response.data.pageInfo?.hasNextPage || false,
          endCursor: response.data.pageInfo?.endCursor || null,
        });
      }
    } catch (error) {
      
      message.error(`Failed to load ${platform} products`);
    } finally {
      setShopifyLoading(false);
    }
  };

  // 搜索外部商店商品（防抖）
  const handleShopifySearch = useCallback(
    debounce((value) => {
      if (!selectedStore?.shop_domain || !selectedStore?.platform) {
        return;
      }
      setShopifyCursor(null);
      fetchExternalProducts(selectedStore.platform, selectedStore.shop_domain, null, value);
    }, 300),
    [selectedStore]
  );

  // 防抖函数
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // 选择Shopify商品
  const handleSelectShopifyProduct = (product) => {
    setSelectedShopifyProduct(product);
    setSelectedLocalProduct(null); // 重置本地商品选择
    setStep(2); // 直接进入配置步骤
    
    // 初始化配置表单
    configForm.setFieldsValue({
      freight_id: undefined,
      customization_id: undefined,
      is_active: true,
    });
  };

  // 选择本系统商品
  const handleSelectLocalProduct = (productId, productData) => {
    setSelectedLocalProduct(productData);
    setProductSelectorVisible(false);
    
    // 重置SKU映射选择（全部改为None）
    const shopifyVariants = selectedShopifyProduct?.variants?.edges || [];
    shopifyVariants.forEach((edge, index) => {
      configForm.setFieldValue(`variant_mapping_${index}`, 'none');
    });
  };

  // 获取本系统商品的变体选项
  const getLocalVariantOptions = (product) => {
    if (!product || !product.variants || !Array.isArray(product.variants)) {
      return [];
    }
    
    return product.variants.map((variant, index) => {
      const displayName = variant.variant_path && variant.variant_path.length > 0
        ? variant.variant_path.join(' - ')
        : 'Single Product';
      
      return {
        value: variant.SKU,
        label: `${displayName} - SKU: ${variant.SKU}`,
        variant: variant,
      };
    });
  };

  // 提交创建或更新连接
  const handleSubmitConnection = async () => {
    try {
      // 验证是否选择了本地商品
      if (!selectedLocalProduct) {
        message.warning('Please select a product first');
        return;
      }
      
      const values = await configForm.validateFields();
      setSubmitting(true);
      
      // 获取SKU映射
      const variantsMapping = {};
      const shopifyVariants = selectedShopifyProduct.variants?.edges || [];
      
      shopifyVariants.forEach((edge, index) => {
        const shopifySku = edge.node.sku;
        const localSku = values[`variant_mapping_${index}`];
        
        // 存储所有variants，未选择的（None）设为null
        if (localSku && localSku !== 'none') {
          variantsMapping[shopifySku] = localSku;
        } else {
          variantsMapping[shopifySku] = null;
        }
      });
      
      if (isEditMode && editingConnection) {
        // 更新模式
        const updateData = {
          variants_mapping: variantsMapping,
          freight_id: values.freight_id,
          customization_id: values.customization_id || null,
          is_active: values.is_active,
        };
        
        await manualProductConnectionAPI.update(editingConnection.id, updateData);
        message.success('Connection updated successfully!');
      } else {
        // 创建模式
        const connectionData = {
          product_id: selectedLocalProduct.id,
          external_product_id: selectedShopifyProduct.legacyResourceId.toString(),
          external_product_title: selectedShopifyProduct.title,
          shop_domain: selectedStore.shop_domain,
          platform: selectedStore.platform,
          variants_mapping: variantsMapping,
          freight_id: values.freight_id,
          customization_id: values.customization_id || null,
          is_active: values.is_active,
        };
        
        await manualProductConnectionAPI.create(connectionData);
        message.success('Connection created successfully!');
      }
      
      setConnectModalVisible(false);
      setIsEditMode(false);
      setEditingConnection(null);
      fetchConnections();
      
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error(isEditMode ? 'Failed to update connection' : 'Failed to create connection');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 编辑连接
  const handleEditConnection = (connection) => {
    try {
      setIsEditMode(true);
      setEditingConnection(connection);
      
      // 从connected_platforms获取Shopify商品信息
      const platformInfo = connection.connected_platforms?.[0];
      if (!platformInfo) {
        message.error('Cannot find platform connection info');
        return;
      }
      
      // 设置已选择的商店信息
      const store = connectedStores.find(s => s.shop_domain === platformInfo.shop_domain);
      if (!store) {
        message.error('Store not found');
        return;
      }
      setSelectedStore(store);
      
      // 使用已存储的信息创建一个简化的shopify product对象（不请求API）
      // 从 variants_mapping 的 keys 构建 variants 信息
      const variantEdges = Object.keys(connection.variants_mapping || {}).map((sku) => ({
        node: {
          id: sku,
          sku: sku,
          title: sku,
          selectedOptions: []
        }
      }));
      
      setSelectedShopifyProduct({
        id: platformInfo.external_id,
        title: platformInfo.title,
        legacyResourceId: platformInfo.external_id,
        media: { edges: [] }, // 编辑模式不显示图片
        variants: { edges: variantEdges }
      });
      
      // 设置已选择的本地商品
      setSelectedLocalProduct(connection.product);
      
      // 进入配置步骤
      setStep(2);
      setConnectModalVisible(true);
      
      // 初始化表单
      configForm.setFieldsValue({
        freight_id: connection.freight_id,
        customization_id: connection.customization_id,
        is_active: connection.is_active,
      });
      
    } catch (error) {
      
      message.error('Failed to load connection data');
    }
  };

  // 删除连接
  const handleDeleteConnection = async (id) => {
    try {
      await manualProductConnectionAPI.delete(id);
      message.success('Connection deleted successfully!');
      fetchConnections();
    } catch (error) {
      message.error('Failed to delete connection');
    }
  };

  // 更新状态
  const handleToggleActive = async (connection) => {
    try {
      await manualProductConnectionAPI.update(connection.id, {
        is_active: !connection.is_active,
      });
      message.success('Status updated successfully!');
      fetchConnections();
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  // 获取商品图片URL
  const getProductImageUrl = (product) => {
    if (!product?.images || product.images.length === 0) {
      return null;
    }
    const coverImage = product.images.find(img => !img.for_variants) || product.images[0];
    return `${API_BASE_URL}/products/${product.id}/images/${coverImage.id}`;
  };

  // 已连接商品列表列定义
  const columns = [
    {
      title: 'Product',
      key: 'local_product',
      width: '25%',
      render: (_, record) => {
        if (!record.product) {
          return null;
        }
        const imageUrl = getProductImageUrl(record.product);
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {imageUrl ? (
              <Image
                src={imageUrl}
                width={50}
                height={50}
                style={{ objectFit: 'cover', marginRight: 12, borderRadius: 4 }}
                preview={false}
              />
            ) : (
              <div style={{ 
                width: 50, 
                height: 50, 
                backgroundColor: '#f0f0f0',
                marginRight: 12,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                color: '#999'
              }}>
                No Image
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <a 
                href={`/product/${record.product.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  fontWeight: 'bold',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.4em',
                  maxHeight: '2.8em',
                  color: BRAND_PRIMARY
                }}
              >
                {record.product.name}
              </a>
            </div>
          </div>
        );
      },
    },
    {
      title: 'External Product',
      key: 'external',
      width: '25%',
      render: (_, record) => {
        const platformInfo = record.connected_platforms?.[0];
        return platformInfo ? (
          <div>
            {renderPlatformTag(platformInfo.platform)}
            <div style={{ fontSize: 12, marginTop: 4 }}>
              <div>{platformInfo.shop_domain}</div>
              {platformInfo.external_url ? (
                <a 
                  href={platformInfo.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: BRAND_PRIMARY,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4em',
                    maxHeight: '2.8em'
                  }}
                >
                  {platformInfo.title}
                </a>
              ) : (
                <div style={{ 
                  color: '#666',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.4em',
                  maxHeight: '2.8em'
                }}>
                  {platformInfo.title}
                </div>
              )}
            </div>
          </div>
        ) : null;
      },
    },
    {
      title: 'Freight & Options',
      key: 'options',
      width: '20%',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 12 }}>
            <strong>Freight:</strong> {record.freight?.name || 'N/A'}
          </div>
          {record.customization && (
            <div style={{ fontSize: 12, marginTop: 4 }}>
              <strong>Custom:</strong> {record.customization.customization_name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: '10%',
      render: (_, record) => (
        <Switch
          checked={record.is_active}
          onChange={() => handleToggleActive(record)}
          size="small"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditConnection(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to delete this connection?"
            onConfirm={() => handleDeleteConnection(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 渲染连接流程的步骤内容
  const renderStepContent = () => {
    if (step === 0) {
      // 步骤1：选择平台
      return (
        <div>
          <h3>Select a Connected Store</h3>
          {connectedStores.length === 0 ? (
            <Alert
              message="No Connected Stores"
              description="You need to connect a Shopify store first."
              type="warning"
              showIcon
            />
          ) : (
            <Table
              dataSource={connectedStores}
              rowKey={(record) => record.id ?? `${record.platform}-${record.shop_domain}`}
              pagination={false}
              columns={[
                {
                  title: 'Platform',
                  dataIndex: 'platform',
                  key: 'platform',
                  render: (platform) => renderPlatformTag(platform),
                  width: 100,
                },
                {
                  title: 'Store Domain',
                  dataIndex: 'shop_domain',
                  key: 'shop_domain',
                },
                {
                  title: 'Connected At',
                  key: 'installed_at',
                  render: (_, record) => formatDate(record?.data?.installed_at),
                  width: 150,
                },
                {
                  title: 'Action',
                  key: 'action',
                  render: (_, record) => (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleSelectStore(record)}
                    >
                      Select Store
                    </Button>
                  ),
                  width: 150,
                },
              ]}
            />
          )}
        </div>
      );
    } else if (step === 1) {
      // 步骤2：选择外部商店商品
      return (
        <div>
          <h3>Select a {selectedStore.platform?.toUpperCase()} Product from {selectedStore.shop_domain}</h3>
          <div style={{ marginBottom: 16 }}>
            <Search
              placeholder="Search product by title..."
              prefix={<SearchOutlined />}
              value={shopifySearch}
              allowClear
              enterButton
              onChange={(e) => setShopifySearch(e.target.value)}
              onSearch={(value) => {
                setShopifySearch(value);
                handleShopifySearch(value);
              }}
            />
          </div>
          <Spin spinning={shopifyLoading}>
            <Table
              dataSource={shopifyProducts}
              rowKey={(record) => record.id}
              pagination={false}
              columns={[
                {
                  title: 'Image',
                  key: 'image',
                  width: 80,
                  render: (_, record) => {
                    const imageUrl = record.media?.edges?.[0]?.node?.image?.url;
                    return imageUrl ? (
                      <Image
                        src={imageUrl}
                        width={50}
                        height={50}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                      />
                    ) : (
                      <div style={{ width: 50, height: 50, backgroundColor: '#f0f0f0', borderRadius: 4 }} />
                    );
                  },
                },
                {
                  title: 'Title',
                  dataIndex: 'title',
                  key: 'title',
                },
                {
                  title: 'Variants',
                  key: 'variants',
                  width: 100,
                  render: (_, record) => (
                    <span>{record.variants?.edges?.length || 0} variants</span>
                  ),
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: 120,
                  render: (_, record) => (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleSelectShopifyProduct(record)}
                    >
                      Select
                    </Button>
                  ),
                },
              ]}
            />
            {shopifyPagination.hasNextPage && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Button
                  onClick={() => {
                    setShopifyCursor(shopifyPagination.endCursor);
                    fetchExternalProducts(
                      selectedStore.platform,
                      selectedStore.shop_domain,
                      shopifyPagination.endCursor,
                      shopifySearch
                    );
                  }}
                >
                  Load More
                </Button>
              </div>
            )}
          </Spin>
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(0)}>Back</Button>
          </div>
        </div>
      );
    } else if (step === 2) {
      // 步骤3：配置SKU映射和选项（包含选择本地商品）
      
      // 安全检查：确保必要的数据存在
      if (!selectedShopifyProduct || !selectedStore) {
        return (
          <div>
            <Alert
              message="Error"
              description="Missing product or store information. Please go back and try again."
              type="error"
              showIcon
            />
            <div style={{ marginTop: 16 }}>
              <Button onClick={() => setStep(1)}>Back</Button>
            </div>
          </div>
        );
      }
      
      const shopifyVariants = selectedShopifyProduct.variants?.edges || [];
      const localVariantOptions = selectedLocalProduct ? getLocalVariantOptions(selectedLocalProduct) : [];
      const supportedChannelIds = selectedLocalProduct?.supported_freight_channels || [];
      const availableChannels = freightChannels.filter(channel => 
        supportedChannelIds.includes(channel.id)
      );
      
      // 在编辑模式下，如果有现有的映射关系，初始化表单
      if (isEditMode && editingConnection && selectedLocalProduct && shopifyVariants.length > 0) {
        shopifyVariants.forEach((edge, index) => {
          const shopifySku = edge.node.sku;
          const mappedSku = editingConnection.variants_mapping[shopifySku];
          if (mappedSku && !configForm.getFieldValue(`variant_mapping_${index}`)) {
            configForm.setFieldValue(`variant_mapping_${index}`, mappedSku);
          }
        });
      }
      
      // 获取商品图片
      const getShopifyImageUrl = () => {
        return selectedShopifyProduct?.media?.edges?.[0]?.node?.image?.url || null;
      };
      
      const getLocalImageUrl = () => {
        if (!selectedLocalProduct?.images || selectedLocalProduct.images.length === 0) {
          return null;
        }
        const coverImage = selectedLocalProduct.images.find(img => !img.for_variants) || selectedLocalProduct.images[0];
        return `${API_BASE_URL}/products/${selectedLocalProduct.id}/images/${coverImage.id}`;
      };
      
      // 构建外部商品链接
      let externalProductUrl = '';
      if (selectedStore.platform === 'shopify') {
        const store_name = selectedStore.shop_domain.split('.')[0];
        externalProductUrl = `https://admin.shopify.com/store/${store_name}/products/${selectedShopifyProduct.legacyResourceId}`;
      } else if (selectedStore.platform === 'woocommerce') {
        externalProductUrl = `https://${selectedStore.shop_domain}/wp-admin/post.php?post=${selectedShopifyProduct.legacyResourceId}&action=edit`;
      }
      
      return (
        <div>
          <h3>{isEditMode ? '' : 'Configure SKU Mapping and Options'}</h3>
          
          {/* 商品信息 - 两栏布局 */}
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              {/* 左栏：Shopify Product */}
              <Col span={12}>
                <div style={{ borderRight: '1px solid #f0f0f0', paddingRight: 16 }}>
                  <h4 style={{ marginBottom: 12, color: '#96bf48' }}>{selectedStore.platform?.toUpperCase()} Product</h4>
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {getShopifyImageUrl() ? (
                      <Image
                        src={getShopifyImageUrl()}
                        width={60}
                        height={60}
                        style={{ objectFit: 'cover', marginRight: 12, borderRadius: 4 }}
                        preview={false}
                      />
                    ) : (
                      <div style={{ 
                        width: 60, 
                        height: 60, 
                        backgroundColor: '#f0f0f0',
                        marginRight: 12,
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        color: '#999'
                      }}>
                        No Image
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <a 
                        href={externalProductUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          fontSize: 14,
                          fontWeight: 'bold',
                          color: BRAND_PRIMARY,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '1.4em'
                        }}
                      >
                        {selectedShopifyProduct.title}
                      </a>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        {selectedStore.shop_domain}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              
              {/* 右栏：CH Product */}
              <Col span={12}>
                <div style={{ paddingLeft: 16 }}>
                  <h4 style={{ marginBottom: 12, color: '#722ed1' }}>Product</h4>
                  {selectedLocalProduct ? (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
                        {getLocalImageUrl() ? (
                          <Image
                            src={getLocalImageUrl()}
                            width={60}
                            height={60}
                            style={{ objectFit: 'cover', marginRight: 12, borderRadius: 4 }}
                            preview={false}
                          />
                        ) : (
                          <div style={{ 
                            width: 60, 
                            height: 60, 
                            backgroundColor: '#f0f0f0',
                            marginRight: 12,
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            color: '#999'
                          }}>
                            No Image
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <a 
                            href={`/product/${selectedLocalProduct.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              fontSize: 14,
                              fontWeight: 'bold',
                              color: BRAND_PRIMARY,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: '1.4em'
                            }}
                          >
                            {selectedLocalProduct.name}
                          </a>
                          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            ID: {selectedLocalProduct.id}
                          </div>
                        </div>
                      </div>
                      <Button 
                        type="primary"
                        size="small"
                        onClick={() => setProductSelectorVisible(true)}
                        style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                      >
                        Change Product
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ 
                        padding: '20px', 
                        textAlign: 'center', 
                        backgroundColor: '#fafafa', 
                        borderRadius: 4,
                        marginBottom: 12
                      }}>
                        <div style={{ fontSize: 14, color: '#999', marginBottom: 12 }}>
                          No product selected
                        </div>
                      </div>
                      <Button 
                        type="primary"
                        size="small"
                        onClick={() => setProductSelectorVisible(true)}
                        style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                      >
                        Select a Product
                      </Button>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Card>
          
          <h4>SKU Mapping</h4>
          <div style={{ marginBottom: 16, maxHeight: 300, overflow: 'auto' }}>
            {shopifyVariants.map((edge, index) => {
              const variant = edge.node;
              const selectedOptions = variant.selectedOptions?.map(opt => `${opt.name}: ${opt.value}`).join(', ');
              
              return (
                <Card key={index} size="small" style={{ marginBottom: 8 }}>
                  <Row gutter={16} align="middle">
                    <Col span={10}>
                      <div style={{ fontSize: 12 }}>
                        <div><strong>Shopify SKU:</strong> {variant.sku || 'N/A'}</div>
                        <div style={{ color: '#666' }}>{selectedOptions || variant.title}</div>
                      </div>
                    </Col>
                    <Col span={2} style={{ textAlign: 'center' }}>
                      <ArrowRightOutlined />
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={`variant_mapping_${index}`}
                        label="Product SKU"
                        initialValue="none"
                        style={{ marginBottom: 0 }}
                      >
                        <Select placeholder="Select an SKU">
                          <Option value="none">None</Option>
                          {localVariantOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              );
            })}
          </div>
          
          <h4>Freight and Customization</h4>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="freight_id"
                label="Freight Channel"
                rules={[{ required: true, message: 'Please select a freight channel' }]}
              >
                <Select placeholder="Select freight channel">
                  {availableChannels.map(channel => (
                    <Option key={channel.id} value={channel.id}>
                      {channel.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="customization_id"
                label="Customization (Optional)"
              >
                <Select placeholder="Select customization (optional)" allowClear>
                  {myCustomizations.map(customization => (
                    <Option key={customization.id} value={customization.id}>
                      {customization.customization_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="is_active"
            label="Active Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
          
          <Space>
            <Button onClick={() => {
              if (isEditMode) {
                setConnectModalVisible(false);
                setIsEditMode(false);
                setEditingConnection(null);
              } else {
                setStep(1);
              }
            }}>
              {isEditMode ? 'Cancel' : 'Back'}
            </Button>
            <Button
              type="primary"
              onClick={handleSubmitConnection}
              loading={submitting}
              disabled={!selectedLocalProduct}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              {isEditMode ? 'Update Connection' : 'Create Connection'}
            </Button>
          </Space>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>
          Manual Product Connections
        </h1>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleStartConnect}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Connect a Product
        </Button>
      </div>

      {/* Connections Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={connections}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          locale={{ emptyText: 'No connections yet. Click "Connect a Product" to start.' }}
        />
      </Card>

      {/* Connect/Edit Product Modal */}
      <Modal
        title={isEditMode ? 'Edit Connection' : `Connect Product - Step ${step + 1}`}
        open={connectModalVisible}
        onCancel={() => {
          setConnectModalVisible(false);
          setIsEditMode(false);
          setEditingConnection(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form form={configForm}>
          {renderStepContent()}
        </Form>
      </Modal>

      {/* Product Selector Modal */}
      <Modal
        title="Select Product"
        open={productSelectorVisible}
        onCancel={() => setProductSelectorVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <ProductSearchSelector
          multiple={false}
          onSelect={handleSelectLocalProduct}
        />
      </Modal>
    </div>
  );
};

export default ManualProductConnect;
